import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { IonButton, IonCheckbox, IonSpinner } from '@ionic/angular/standalone';
import { FormlyModule } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { environment } from '@env/environment';
import { FormlyFieldAnyComponent } from '@app/shared/components/formly-components/formly-field-any/formly-field-any.component';

interface RoleScopeTable {
  name: string;
  rows?: string;
  comment?: string;
}

interface RoleScopeResponse {
  success: boolean;
  message?: string;
  data?: {
    actions: string[];
    tables: RoleScopeTable[];
  };
}

@Component({
  selector: 'app-formly-field-permissions',
  templateUrl: './formly-field-permissions.component.html',
  styleUrls: ['./formly-field-permissions.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormlyModule,
    TranslateModule,
    IonButton,
    IonCheckbox,
    IonSpinner,
  ],
})
export class FormlyFieldPermissionsComponent extends FormlyFieldAnyComponent implements OnInit, OnDestroy {
  public actions: string[] = [];
  public tables: RoleScopeTable[] = [];
  public isLoading = true;
  public loadError = false;

  private selectedPermissions = new Set<string>();
  private valueSubscription?: Subscription;

  private static scopeCache: RoleScopeResponse['data'] | null = null;
  private static scopePromise: Promise<RoleScopeResponse['data'] | null> | null = null;

  private readonly http = inject(HttpClient);

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.valueSubscription = this.formControl.valueChanges.subscribe((value) => {
      this.syncSelectionFromValue(value);
      this.changeDetectorRef.markForCheck();
    });

    this.syncSelectionFromValue(this.formControl.value);
    await this.loadScope();
  }

  ngOnDestroy(): void {
    this.valueSubscription?.unsubscribe();
  }

  public get isReadonly(): boolean {
    return Boolean(this.options.formState?.disabled || this.field.props?.disabled || this.field.props?.readonly);
  }

  public trackByAction(index: number, action: string): string {
    return action;
  }

  public trackByTable(index: number, table: RoleScopeTable): string {
    return table.name;
  }

  public isPermissionGranted(tableName: string, action: string): boolean {
    return this.selectedPermissions.has(this.buildPermissionKey(tableName, action));
  }

  public onPermissionChange(tableName: string, action: string, checked: boolean): void {
    if (this.isReadonly) {
      return;
    }

    const key = this.buildPermissionKey(tableName, action);
    if (checked) {
      this.selectedPermissions.add(key);
    } else {
      this.selectedPermissions.delete(key);
    }

    this.writeValue();
  }

  public async retryLoad(): Promise<void> {
    await this.loadScope(true);
  }

  public getActionLabel(action: string): string {
    return action
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (value) => value.toUpperCase());
  }

  private async loadScope(forceReload = false): Promise<void> {
    this.isLoading = true;
    this.loadError = false;
    this.changeDetectorRef.markForCheck();

    try {
      const scope = await this.getScopeData(forceReload);
      this.actions = scope?.actions || [];
      this.tables = scope?.tables || [];
      this.loadError = !scope;
    } catch (error) {
      console.error('Failed to load permissions scope:', error);
      this.actions = [];
      this.tables = [];
      this.loadError = true;
    } finally {
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  private async getScopeData(forceReload: boolean): Promise<RoleScopeResponse['data'] | null> {
    if (!forceReload && FormlyFieldPermissionsComponent.scopeCache) {
      return FormlyFieldPermissionsComponent.scopeCache;
    }

    if (!forceReload && FormlyFieldPermissionsComponent.scopePromise) {
      return FormlyFieldPermissionsComponent.scopePromise;
    }

    FormlyFieldPermissionsComponent.scopePromise = firstValueFrom(
      this.http.get<RoleScopeResponse>(`${environment.apiUrl}/roles/scope`)
    )
      .then((response) => {
        if (!response?.success || !response.data) {
          return null;
        }
        FormlyFieldPermissionsComponent.scopeCache = response.data;
        return response.data;
      })
      .finally(() => {
        FormlyFieldPermissionsComponent.scopePromise = null;
      });

    return FormlyFieldPermissionsComponent.scopePromise;
  }

  private syncSelectionFromValue(value: unknown): void {
    const permissions = Array.isArray(value)
      ? value.filter((entry): entry is string => typeof entry === 'string' && entry.includes(':'))
      : [];

    this.selectedPermissions = new Set(permissions);
  }

  private writeValue(): void {
    const nextValue = Array.from(this.selectedPermissions).sort((left, right) => left.localeCompare(right));
    this.formControl.setValue(nextValue);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    this.onChange(nextValue);
    this.changeDetectorRef.markForCheck();
  }

  private buildPermissionKey(tableName: string, action: string): string {
    return `${tableName}:${action}`;
  }
}